import React, {useState, useEffect} from 'react'
import moment from 'moment'
import Pagination from 'react-responsive-pagination'
import RecordPerPageSelector from '../../../../common/components/selectors/RecordPerPageSelector'
import {TableHeader} from '../../../../common/components/TableHeader'
import {WorkOrderClient} from '../../../../infrastracture/api/WorkOrderClient'

interface Props {
  show: boolean
  handleClose: () => void
  workOrderId: string
}

const ExpectedOnSiteAuditModal: React.FC<Props> = ({show, handleClose, workOrderId}) => {
  const [expectedOnSiteAudits, setExpectedOnSiteAudits] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalRecords: 0,
    totalPages: 1,
    skip: 0,
    take: 5,
    reset: true,
    sort: 'created',
    direction: 'desc',
  })

  const fetchData = async () => {
    if (!workOrderId || !show) return

    setLoading(true)
    try {
      // Convert direction to enum value expected by backend
      const directionEnum = pagination.direction === 'asc' ? 'Ascending' : 'Descending'

      const response = await WorkOrderClient.GetExpectedOnSiteAudit(
        workOrderId,
        pagination.skip,
        pagination.take,
        pagination.sort,
        directionEnum
      )

      if (response.successful && response.data) {
        setExpectedOnSiteAudits(response.data.data || [])
        const totalPages = Math.ceil((response.data.total || 0) / pagination.take)
        setPagination((prev) => ({
          ...prev,
          totalRecords: response.data.total || 0,
          totalPages,
          reset: false,
        }))
      }
    } catch (error) {
      console.error('Error fetching expected on site audits:', error)
      setExpectedOnSiteAudits([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (show && workOrderId) {
      fetchData()
    }
  }, [show, workOrderId, pagination.skip, pagination.take, pagination.sort, pagination.direction])

  if (!show) return null

  return (
    <>
      <div
        className={`modal fade ${show ? 'show' : ''}`}
        tabIndex={-1}
        id='expectedOnSiteAudit_modal'
        style={{display: show ? 'block' : 'none'}}
      >
        <div className='modal-dialog modal-dialog-centered modal-lg'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>
                Expected On Site Change History
                <span className='badge badge-square badge-primary ms-3'>
                  {pagination.totalRecords}
                </span>
              </h5>
              <div
                className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                aria-label='Close'
                onClick={handleClose}
              >
                <span className='svg-icon svg-icon-2x'></span>
              </div>
            </div>

            <div className='modal-body'>
              {loading ? (
                <div className='text-center py-10'>
                  <span className='spinner-border spinner-border-lg align-middle'></span>
                </div>
              ) : expectedOnSiteAudits.length > 0 ? (
                <>
                  <div className='table-responsive'>
                    <table className='table align-middle gs-0 gy-4'>
                      <TableHeader
                        sort={pagination.sort}
                        direction={pagination.direction}
                        columns={[
                          {name: 'Previous Date', sort: 'dueDateFrom', width: '20'},
                          {name: 'New Date', sort: 'dueDateTo', width: '20'},
                          {name: 'Reason', sort: 'reason', width: '30'},
                          {name: 'Updated By', sort: 'updatedByName', width: '20'},
                        ]}
                        onUpdate={(sort: any, direction: any) => {
                          setPagination({
                            ...pagination,
                            sort,
                            direction,
                            skip: 0,
                            currentPage: 1,
                            reset: true,
                          })
                        }}
                      />
                      <tbody>
                        {expectedOnSiteAudits.map((audit, index) => (
                          <tr key={index}>
                            <td>
                              <span className='text-dark fw-bolder d-block fs-6'>
                                {audit.dueDateFrom
                                  ? moment(audit.dueDateFrom).format('DD/MM/YYYY HH:mm')
                                  : 'None'}
                              </span>
                            </td>
                            <td>
                              <span className='text-dark fw-bolder d-block fs-6'>
                                {audit.dueDateTo
                                  ? moment(audit.dueDateTo).format('DD/MM/YYYY HH:mm')
                                  : 'None'}
                              </span>
                            </td>
                            <td>
                              <span className='text-dark d-block fs-6'>
                                {audit.reason || <i>Not specified</i>}
                              </span>
                            </td>
                            <td>
                              <span className='text-dark fw-bolder d-block fs-6'>
                                {audit.updatedByName || 'Unknown'}
                              </span>
                            </td>

                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className='card-footer px-1 py-4'>
                    <div className='d-flex justify-content-between'>
                      <div style={{minWidth: '200px'}}>
                        <RecordPerPageSelector
                          placeholder='Rows per page'
                          onChange={(name, value) => {
                            setPagination({
                              ...pagination,
                              take: value,
                              skip: 0,
                              currentPage: 1,
                              reset: true,
                            })
                          }}
                          defaultValue={{value: pagination.take, label: pagination.take}}
                        />
                      </div>
                      <div>
                        <Pagination
                          current={pagination.currentPage}
                          total={pagination.totalPages}
                          onPageChange={(page) => {
                            setPagination({
                              ...pagination,
                              currentPage: page,
                              skip: page === 1 ? 0 : (page - 1) * pagination.take,
                              reset: true,
                            })
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className='text-center py-10'>
                  <span className='text-muted fs-6'>No expected on site change history available.</span>
                </div>
              )}
            </div>

            <div className='modal-footer'>
              <button type='button' className='btn btn-light' onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      {show && <div className='modal-backdrop fade show'></div>}
    </>
  )
}

export default ExpectedOnSiteAuditModal
